import { Component, Input, Output, EventEmitter, ChangeDetectorRef, ViewChild, ElementRef, inject } from '@angular/core';
import { LogError, RunView } from '@memberjunction/core';
import { marketingSocialPostEntity } from 'mj_generatedentities';
import { ContentStatus } from '../../types';

/** A single active mention available to insert into the post body. */
interface MentionPickerRow {
    ID: string;
    DisplayName: string;
    EntityType: string;
    LocalizedName: string;
}

/**
 * Matches inline mention tokens of the form `@[text](mentionId)`.
 * MUST stay in sync with the canonical `MENTION_TOKEN` in the backend resolver
 * (`packages/Marketing/src/business/mentions/mentionResolver.ts`) — that resolver
 * is the source of truth for what counts as a real mention at publish time. The
 * id group is `[^)]+` (non-empty), so an empty-id token is NOT treated as a
 * mention here, matching the resolver (which leaves it as raw text in the body).
 */
const MENTION_TOKEN_RE = /@\[([^\]]*)\]\(([^)]+)\)/g;

/**
 * Pure: collapse mention tokens to their visible text so character counts
 * reflect what readers actually see. `@[Buffer](abc-123)` → `Buffer`.
 */
function stripMentionTokens(body: string): string {
    if (!body) return '';
    return body.replace(MENTION_TOKEN_RE, (_match, text: string) => text);
}

const PLATFORM_CHAR_LIMITS: Record<string, number> = {
    'LinkedIn': 3000,
    'Twitter/X': 280,
    'Facebook': 63206,
    'Instagram': 2200,
    'YouTube': 5000,
};

const PLATFORM_ICONS: Record<string, string> = {
    'LinkedIn': 'fa-brands fa-linkedin',
    'Twitter/X': 'fa-brands fa-x-twitter',
    'Facebook': 'fa-brands fa-facebook',
    'Instagram': 'fa-brands fa-instagram',
    'YouTube': 'fa-brands fa-youtube',
};

/** Pure: compute character limit ratio and color class */
function charLimitInfo(body: string, platformName: string): { count: number; limit: number | null; pct: number; colorClass: string } {
    const count = body?.length || 0;
    const limit = PLATFORM_CHAR_LIMITS[platformName] ?? null;
    if (limit == null) return { count, limit: null, pct: 0, colorClass: 'sp-panel__char-bar--green' };
    const pct = count / limit;
    const colorClass = pct > 1 ? 'sp-panel__char-bar--red'
        : pct >= 0.8 ? 'sp-panel__char-bar--amber'
        : 'sp-panel__char-bar--green';
    return { count, limit, pct: Math.min(pct, 1), colorClass };
}

/** Pure: format a Date for datetime-local input */
function toDatetimeLocalString(d: Date | null): string {
    if (!d) return '';
    const dt = new Date(d);
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
}

@Component({
    standalone: false,
    selector: 'mkt-social-post-detail-panel',
    template: `
    <div class="sp-panel">
      <!-- Header -->
      <div class="sp-panel__header">
        <div class="sp-panel__header-icon">
          <i [class]="platformIcon"></i>
        </div>
        <div class="sp-panel__header-info">
          <select class="sp-panel__platform-select"
                  [ngModel]="post.SocialPlatformID"
                  (ngModelChange)="onPlatformChange($event)">
            <option *ngFor="let p of socialPlatforms" [value]="p.ID">{{ p.Name }}</option>
          </select>
          <div class="sp-panel__meta-row">
            <span class="sp-panel__badge" [class]="'sp-panel__badge--' + statusName.toLowerCase()">
              {{ statusName }}
            </span>
          </div>
        </div>
        <div class="sp-panel__header-actions">
          <button class="sp-panel__icon-btn" (click)="close.emit()" title="Close">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>
      </div>

      <!-- Dates -->
      <div class="sp-panel__dates">
        <span class="sp-panel__date-item">
          <i class="fa-regular fa-clock"></i> Created: {{ post.__mj_CreatedAt | date:'medium' }}
        </span>
        <span class="sp-panel__date-item sp-panel__date-item--editable">
          <i class="fa-regular fa-calendar"></i>
          <label class="sp-panel__date-label">Scheduled:</label>
          <input type="datetime-local" class="sp-panel__date-input"
                 [ngModel]="scheduledDateLocal"
                 (ngModelChange)="onScheduledDateChange($event)">
        </span>
        <span *ngIf="post.PublishedDate" class="sp-panel__date-item">
          <i class="fa-solid fa-paper-plane"></i> Published: {{ post.PublishedDate | date:'medium' }}
        </span>
      </div>

      <!-- Blog link -->
      <div *ngIf="post.BlogID" class="sp-panel__blog-link" (click)="openBlog.emit(post.BlogID!)">
        <i class="fa-solid fa-link"></i>
        <span>{{ post.Blog || 'Linked Blog' }}</span>
        <i class="fa-solid fa-arrow-up-right-from-square sp-panel__blog-link-arrow"></i>
      </div>

      <!-- Body -->
      <div class="sp-panel__body">
        <div class="sp-panel__body-toolbar">
          <button class="sp-panel__mention-btn"
                  (click)="toggleMentionPicker()"
                  [disabled]="!post.CompanyID"
                  [title]="post.CompanyID ? 'Insert a LinkedIn @mention' : 'No company on this post'">
            <i class="fa-solid fa-at"></i> Mention
          </button>
        </div>

        <!-- Mention picker -->
        <div class="sp-panel__mention-picker" *ngIf="mentionPickerOpen">
          <input class="sp-panel__mention-search" type="text"
                 placeholder="Search mentions..."
                 [(ngModel)]="mentionSearch"
                 (ngModelChange)="onMentionSearchChange()" />
          <div class="sp-panel__mention-list">
            <div *ngIf="mentionsLoading" class="sp-panel__mention-empty">
              <i class="fa-solid fa-spinner fa-spin"></i> Loading…
            </div>
            <div *ngIf="!mentionsLoading && filteredMentions.length === 0" class="sp-panel__mention-empty">
              {{ mentionError || 'No active mentions. Add some in Foundation · Mention directory.' }}
            </div>
            <button *ngFor="let m of filteredMentions"
                    class="sp-panel__mention-item"
                    (click)="insertMention(m)">
              <i class="fa-solid sp-panel__mention-item-icon"
                 [class.fa-user]="m.EntityType === 'Person'"
                 [class.fa-building]="m.EntityType === 'Organization'"></i>
              <span class="sp-panel__mention-item-name">{{ m.DisplayName }}</span>
              <span class="sp-panel__mention-item-local">{{ m.LocalizedName }}</span>
            </button>
          </div>
        </div>

        <textarea #bodyTextarea class="sp-panel__textarea" [(ngModel)]="post.Body"
                  (ngModelChange)="onBodyChange()"
                  rows="12" placeholder="Post body..."></textarea>
        <div class="sp-panel__char-bar-track">
          <div class="sp-panel__char-bar-fill"
               [class]="charInfo.colorClass"
               [style.width.%]="charInfo.limit ? charInfo.pct * 100 : 0">
          </div>
        </div>
        <div class="sp-panel__char-count" [class]="charInfo.colorClass + '-text'">
          {{ charInfo.count }}<span *ngIf="charInfo.limit != null"> / {{ charInfo.limit }}</span> characters
        </div>
      </div>

      <!-- Footer actions -->
      <div class="sp-panel__footer">
        <button *ngIf="statusName === 'Draft'"
                class="sp-panel__btn sp-panel__btn--primary"
                (click)="onApprove()">
          <i class="fa-solid fa-check"></i> Approve
        </button>
        <button *ngIf="statusName === 'Approved' && !isAutoPublish"
                class="sp-panel__btn sp-panel__btn--primary"
                (click)="publishPost.emit(post.ID)">
          <i class="fa-solid fa-paper-plane"></i> Publish
        </button>
        <button *ngIf="statusName === 'Approved' && post.ScheduledPublishDate && !post.PublishedDate"
                class="sp-panel__btn sp-panel__btn--accent"
                (click)="schedulePost.emit(post.ID)">
          <i class="fa-regular fa-calendar-check"></i> Schedule
        </button>
        <div class="sp-panel__footer-spacer"></div>
        <span *ngIf="saving" class="sp-panel__saving-indicator">
          <i class="fa-solid fa-spinner fa-spin"></i> Saving...
        </span>
        <button class="sp-panel__btn sp-panel__btn--danger"
                (click)="deletePost.emit(post.ID)">
          <i class="fa-solid fa-trash"></i> Delete
        </button>
      </div>
    </div>
  `,
    styles: [`
    .sp-panel { display: flex; flex-direction: column; height: 100%; background: var(--mj-bg-surface); }

    /* ── Header ─────────────────────────────────────── */
    .sp-panel__header { display: flex; align-items: center; gap: 10px; padding: 16px 20px; border-bottom: 1px solid var(--mj-border-default); }
    .sp-panel__header-icon { font-size: 1.2rem; color: var(--mj-color-violet-500); width: 28px; text-align: center; }
    .sp-panel__header-info { flex: 1; min-width: 0; }
    .sp-panel__platform-select {
      font-size: 16px; font-weight: 700; color: var(--mj-text-primary);
      background: var(--mj-bg-surface); border: 1px solid transparent; border-radius: 4px;
      padding: 2px 6px; cursor: pointer; font-family: inherit; max-width: 200px;
    }
    .sp-panel__platform-select:hover { border-color: var(--mj-border-default); }
    .sp-panel__platform-select:focus { border-color: var(--mj-brand-primary); outline: none; }
    .sp-panel__meta-row { display: flex; align-items: center; gap: 8px; margin-top: 4px; }
    .sp-panel__badge { padding: 2px 8px; border-radius: 10px; font-size: 12px; font-weight: 600; background: var(--mj-bg-page); color: var(--mj-text-secondary); }
    .sp-panel__badge--draft { background: var(--mj-status-warning-bg); color: var(--mj-status-warning); }
    .sp-panel__badge--approved { background: var(--mj-status-success-bg); color: var(--mj-color-success-400); }
    .sp-panel__badge--published { background: var(--mj-status-info-bg); color: var(--mj-color-info-500); }
    .sp-panel__badge--scheduled { background: var(--mj-status-info-bg); color: var(--mj-color-info-500); }
    .sp-panel__header-actions { display: flex; gap: 4px; }
    .sp-panel__icon-btn { background: none; border: none; cursor: pointer; padding: 6px 8px; border-radius: 4px; color: var(--mj-text-secondary); }
    .sp-panel__icon-btn:hover { background: var(--mj-bg-page); color: var(--mj-text-primary); }

    /* ── Dates ───────────────────────────────────────── */
    .sp-panel__dates { display: flex; flex-wrap: wrap; gap: 12px; padding: 10px 20px; background: var(--mj-bg-page); border-bottom: 1px solid var(--mj-border-default); font-size: 12px; color: var(--mj-text-secondary); align-items: center; }
    .sp-panel__date-item { display: flex; align-items: center; gap: 4px; }
    .sp-panel__date-item--editable { gap: 6px; }
    .sp-panel__date-label { margin: 0; white-space: nowrap; }
    .sp-panel__date-input {
      font-size: 12px; font-family: inherit; color: var(--mj-text-primary);
      background: var(--mj-bg-surface); border: 1px solid var(--mj-border-default); border-radius: 4px;
      padding: 2px 6px; cursor: pointer;
    }
    .sp-panel__date-input:focus { border-color: var(--mj-brand-primary); outline: none; }

    /* ── Blog link ───────────────────────────────────── */
    .sp-panel__blog-link {
      display: flex; align-items: center; gap: 6px; padding: 8px 20px;
      font-size: 13px; color: var(--mj-brand-primary); cursor: pointer;
      border-bottom: 1px solid var(--mj-border-default); background: var(--mj-bg-surface);
    }
    .sp-panel__blog-link:hover { background: var(--mj-bg-page); text-decoration: underline; }
    .sp-panel__blog-link-arrow { font-size: 10px; opacity: 0.6; }

    /* ── Body ────────────────────────────────────────── */
    .sp-panel__body { flex: 1; overflow-y: auto; padding: 20px; }
    .sp-panel__textarea {
      width: 100%; padding: 10px; border: 1px solid var(--mj-border-default); border-radius: 6px;
      font-size: 14px; font-family: inherit; resize: vertical; box-sizing: border-box;
      background: var(--mj-bg-page); color: var(--mj-text-primary); line-height: 1.6;
    }
    .sp-panel__textarea:focus { border-color: var(--mj-brand-primary); outline: none; }

    /* ── Body toolbar + mention picker ───────────────── */
    .sp-panel__body-toolbar { display: flex; gap: 6px; margin-bottom: 8px; }
    .sp-panel__mention-btn { display: inline-flex; align-items: center; gap: 5px; padding: 5px 10px; border: 1px solid var(--mj-border-default); border-radius: 6px; background: var(--mj-bg-surface); color: var(--mj-text-primary); font-size: 12px; font-family: inherit; cursor: pointer; }
    .sp-panel__mention-btn:hover:not(:disabled) { background: var(--mj-bg-page); border-color: var(--mj-brand-primary); }
    .sp-panel__mention-btn:disabled { opacity: 0.5; cursor: not-allowed; }
    .sp-panel__mention-picker { border: 1px solid var(--mj-brand-primary); border-radius: 6px; background: var(--mj-bg-surface); margin-bottom: 8px; overflow: hidden; }
    .sp-panel__mention-search { width: 100%; box-sizing: border-box; padding: 8px 10px; border: none; border-bottom: 1px solid var(--mj-border-default); font-size: 13px; font-family: inherit; background: var(--mj-bg-surface); color: var(--mj-text-primary); }
    .sp-panel__mention-search:focus { outline: none; }
    .sp-panel__mention-list { max-height: 220px; overflow-y: auto; }
    .sp-panel__mention-empty { padding: 14px; text-align: center; font-size: 12.5px; color: var(--mj-text-disabled); }
    .sp-panel__mention-item { display: flex; align-items: center; gap: 8px; width: 100%; text-align: left; padding: 8px 12px; border: none; border-bottom: 1px solid var(--mj-border-default); background: transparent; cursor: pointer; font-family: inherit; }
    .sp-panel__mention-item:last-child { border-bottom: none; }
    .sp-panel__mention-item:hover { background: var(--mj-bg-page); }
    .sp-panel__mention-item-icon { color: var(--mj-text-secondary); width: 14px; flex-shrink: 0; }
    .sp-panel__mention-item-name { font-size: 13px; color: var(--mj-text-primary); font-weight: 500; }
    .sp-panel__mention-item-local { font-size: 11.5px; color: var(--mj-text-secondary); margin-left: auto; }

    /* ── Character limit bar ─────────────────────────── */
    .sp-panel__char-bar-track {
      height: 3px; width: 100%; background: var(--mj-border-default); border-radius: 2px; margin-top: 6px; overflow: hidden;
    }
    .sp-panel__char-bar-fill { height: 100%; border-radius: 2px; transition: width 0.15s ease, background-color 0.15s ease; }
    .sp-panel__char-bar--green { background-color: var(--mj-color-success-400); }
    .sp-panel__char-bar--amber { background-color: var(--mj-status-warning); }
    .sp-panel__char-bar--red { background-color: var(--mj-status-error); }
    .sp-panel__char-count { font-size: 11px; color: var(--mj-text-disabled); margin-top: 4px; }
    .sp-panel__char-bar--green-text { color: var(--mj-text-disabled); }
    .sp-panel__char-bar--amber-text { color: var(--mj-status-warning); }
    .sp-panel__char-bar--red-text { color: var(--mj-status-error); font-weight: 600; }

    /* ── Footer ──────────────────────────────────────── */
    .sp-panel__footer { display: flex; gap: 8px; padding: 12px 20px; border-top: 1px solid var(--mj-border-default); align-items: center; }
    .sp-panel__footer-spacer { flex: 1; }
    .sp-panel__saving-indicator { font-size: 12px; color: var(--mj-text-disabled); display: flex; align-items: center; gap: 4px; }
    .sp-panel__btn { padding: 8px 16px; border: 1px solid var(--mj-border-default); border-radius: 6px; font-size: 13px; cursor: pointer; background: var(--mj-bg-surface); color: var(--mj-text-primary); display: flex; align-items: center; gap: 6px; }
    .sp-panel__btn:hover { background: var(--mj-bg-page); }
    .sp-panel__btn--primary { background: var(--mj-brand-primary); color: var(--mj-text-inverse); border-color: var(--mj-brand-primary); }
    .sp-panel__btn--primary:hover { filter: brightness(1.1); }
    .sp-panel__btn--accent { background: var(--mj-color-info-500); color: var(--mj-text-inverse); border-color: var(--mj-color-info-500); }
    .sp-panel__btn--accent:hover { filter: brightness(1.1); }
    .sp-panel__btn--danger { color: var(--mj-status-error); border-color: color-mix(in srgb, var(--mj-status-error) 30%, transparent); }
    .sp-panel__btn--danger:hover { background: var(--mj-status-error-bg); }
  `]
})
export class SocialPostDetailPanelComponent {
    private cdr = inject(ChangeDetectorRef);

    @Input() post!: marketingSocialPostEntity;
    @Input() contentStatuses: ContentStatus[] = [];
    @Input() socialPlatforms: { ID: string; Name: string }[] = [];
    @Input() channelConfigs: { SocialPlatformID: string | null; AutoPublish: boolean }[] = [];

    @Output() close = new EventEmitter<void>();
    @Output() statusChange = new EventEmitter<{ postId: string; newStatus: string }>();
    @Output() deletePost = new EventEmitter<string>();
    @Output() postSaved = new EventEmitter<string>();
    @Output() publishPost = new EventEmitter<string>();
    @Output() schedulePost = new EventEmitter<string>();
    @Output() openBlog = new EventEmitter<string>();

    saving = false;
    private bodySaveTimer: ReturnType<typeof setTimeout> | null = null;

    @ViewChild('bodyTextarea') bodyTextarea?: ElementRef<HTMLTextAreaElement>;

    // ── @-mention picker state ──
    mentionPickerOpen = false;
    mentionSearch = '';
    mentionsLoading = false;
    mentionError = '';
    mentions: MentionPickerRow[] = [];
    private mentionsLoadedForCompany: string | null = null;
    /** Cursor offset captured when the picker opens, so insertion lands where the user was typing. */
    private savedCursorPos = 0;

    get statusName(): string {
        if (!this.post || !this.contentStatuses.length) return 'Unknown';
        const status = this.contentStatuses.find(s => s.ID === this.post.ContentStatusID);
        return status?.Name || 'Unknown';
    }

    get platformName(): string {
        return this.socialPlatforms.find(p => p.ID === this.post.SocialPlatformID)?.Name
            || this.post.SocialPlatform
            || '';
    }

    get platformIcon(): string {
        return PLATFORM_ICONS[this.platformName] || 'fa-solid fa-share-nodes';
    }

    get charInfo(): { count: number; limit: number | null; pct: number; colorClass: string } {
        // Count the RESOLVED text — mention tokens render as just their visible
        // label on the platform, so `@[Buffer](id)` costs 6 chars, not the full token.
        return charLimitInfo(stripMentionTokens(this.post.Body), this.platformName);
    }

    get scheduledDateLocal(): string {
        return toDatetimeLocalString(this.post.ScheduledPublishDate);
    }

    get isAutoPublish(): boolean {
        const config = this.channelConfigs.find(c => c.SocialPlatformID === this.post.SocialPlatformID);
        return config?.AutoPublish ?? false;
    }

    async onPlatformChange(newPlatformId: string): Promise<void> {
        this.post.SocialPlatformID = newPlatformId;
        await this.savePost();
    }

    async onScheduledDateChange(value: string): Promise<void> {
        this.post.ScheduledPublishDate = value ? new Date(value) : null;
        await this.savePost();
    }

    onBodyChange(): void {
        // Debounce body saves — 800ms after last keystroke
        if (this.bodySaveTimer) clearTimeout(this.bodySaveTimer);
        this.bodySaveTimer = setTimeout(() => this.savePost(), 800);
    }

    onApprove(): void {
        this.statusChange.emit({ postId: this.post.ID, newStatus: 'Approved' });
    }

    /** Refresh the filtered picker list as the user types in the search box. */
    onMentionSearchChange(): void {
        this.cdr.detectChanges();
    }

    get filteredMentions(): MentionPickerRow[] {
        const q = this.mentionSearch.trim().toLowerCase();
        if (!q) return this.mentions;
        return this.mentions.filter(m =>
            m.DisplayName.toLowerCase().includes(q) ||
            m.LocalizedName.toLowerCase().includes(q),
        );
    }

    async toggleMentionPicker(): Promise<void> {
        if (this.mentionPickerOpen) {
            this.mentionPickerOpen = false;
            return;
        }
        // Remember where the cursor was before the textarea loses focus.
        const el = this.bodyTextarea?.nativeElement;
        this.savedCursorPos = el ? el.selectionStart : (this.post.Body?.length ?? 0);
        this.mentionPickerOpen = true;
        this.mentionSearch = '';
        await this.loadMentions();
    }

    private async loadMentions(): Promise<void> {
        const companyId = this.post.CompanyID;
        if (!companyId) {
            this.mentionError = 'This post has no company set.';
            return;
        }
        if (this.mentionsLoadedForCompany === companyId) return; // cached for this company
        this.mentionsLoading = true;
        this.mentionError = '';
        this.cdr.detectChanges();
        try {
            const rv = new RunView();
            const result = await rv.RunView<MentionPickerRow>({
                EntityName: 'Social Mentions',
                ExtraFilter: `CompanyID='${companyId}' AND Status='Active'`,
                OrderBy: 'DisplayName ASC',
                ResultType: 'simple',
                Fields: ['ID', 'DisplayName', 'EntityType', 'LocalizedName'],
                MaxRows: 1000,
            });
            if (result.Success) {
                this.mentions = result.Results;
                this.mentionsLoadedForCompany = companyId;
            } else {
                this.mentionError = result.ErrorMessage || 'Failed to load mentions.';
            }
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : String(err);
            LogError(`[SocialPostDetailPanel] loadMentions failed: ${msg}`);
            this.mentionError = msg;
        } finally {
            this.mentionsLoading = false;
            this.cdr.detectChanges();
        }
    }

    insertMention(m: MentionPickerRow): void {
        const token = `@[${m.LocalizedName}](${m.ID})`;
        const body = this.post.Body ?? '';
        const pos = Math.min(Math.max(this.savedCursorPos, 0), body.length);
        this.post.Body = body.slice(0, pos) + token + body.slice(pos);
        this.mentionPickerOpen = false;

        // Persist via the existing debounced save path.
        this.onBodyChange();

        // Restore focus and place the cursor after the inserted token.
        const newPos = pos + token.length;
        this.savedCursorPos = newPos;
        Promise.resolve().then(() => {
            const el = this.bodyTextarea?.nativeElement;
            if (el) {
                el.focus();
                el.setSelectionRange(newPos, newPos);
            }
            this.cdr.detectChanges();
        });
    }

    private async savePost(): Promise<void> {
        this.saving = true;
        this.cdr.detectChanges();
        const saved = await this.post.Save();
        this.saving = false;
        if (saved) {
            this.postSaved.emit(this.post.ID);
        }
        this.cdr.detectChanges();
    }
}
